// The History popup's single timeline for a Documents-tab document: approval
// markers, file versions, policy-document (adoption) versions and the earlier
// conversations between approvals, newest first. Pure so the order is
// unit-testable (the portals have no DOM test harness).
//
// Stamps are ISO strings straight from PostgREST and are compared as strings,
// never reformatted (see partitionThread in @l2u/shared/threadPartition).

import { visibleAt } from './commentThread.ts';
import {
  orderAdoptionHistory,
  orderFileHistory,
  type AdoptionHistoryItem,
  type AdoptionVersion,
  type FileHistoryItem,
  type FileVersion,
} from './fileHistory.ts';
import type { SubmissionComment } from './types.ts';

export type TimelineItem =
  | { kind: 'approval'; at: string }
  | { kind: 'file'; at: string; version: FileVersion }
  | { kind: 'adoption'; at: string; version: AdoptionVersion }
  | {
      kind: 'conversation';
      at: string;
      index: number;
      messages: SubmissionComment[];
      first: string;
      last: string;
    };

// Tie order at the same instant: the approval sits above the conversation it
// closed, and both above a file uploaded at that moment.
const TIE_RANK: Record<TimelineItem['kind'], number> = {
  approval: 0,
  conversation: 1,
  file: 2,
  adoption: 3,
};

/** Newest first by `at`; ties: approval, conversation, file, adoption. A file is placed at created_at, an adoption at recorded_at, a conversation at its last message (visibleAt); conversations with no messages are skipped. */
export function buildHistoryTimeline(args: {
  files: FileHistoryItem[];
  adoptions: AdoptionHistoryItem[];
  approvals: readonly string[];
  earlier: readonly SubmissionComment[][];
}): TimelineItem[] {
  const items: TimelineItem[] = [];
  for (const at of args.approvals) items.push({ kind: 'approval', at });
  args.earlier.forEach((messages, index) => {
    if (messages.length === 0) return;
    const stamps = messages.map(visibleAt).sort();
    const first = stamps[0]!;
    const last = stamps[stamps.length - 1]!;
    items.push({ kind: 'conversation', at: last, index, messages, first, last });
  });
  for (const version of orderFileHistory(args.files)) {
    items.push({ kind: 'file', at: version.created_at, version });
  }
  for (const version of orderAdoptionHistory(args.adoptions)) {
    items.push({ kind: 'adoption', at: version.recorded_at, version });
  }
  // Array.prototype.sort is stable, so same-kind ties keep their input order.
  return items.sort((a, b) => {
    if (a.at !== b.at) return a.at < b.at ? 1 : -1;
    return TIE_RANK[a.kind] - TIE_RANK[b.kind];
  });
}

/** "N messages" / "1 message". */
export function messageCountLabel(n: number): string {
  return n === 1 ? '1 message' : `${n} messages`;
}
